import * as i0 from '@angular/core';
import { Pipe, NgModule, Directive, isDevMode, Injectable, Optional, Inject } from '@angular/core';
import { get, filter, includes, find, isEmpty, merge, isString, debounce } from 'lodash-es';
import { throwError, timer, Subscription, BehaviorSubject, interval, Subject } from 'rxjs';
import { __decorate } from 'tslib';
import * as i1 from '@angular/common';
import * as i2 from '@upr-web-primer/storage';
import { By } from '@angular/platform-browser';
import { takeUntil } from 'rxjs/operators';

class PriceCentsPipe {
    constructor() {
        this.testText = new RegExp('[a-z|A-Z]', 'gi');
    }
    transform(price) {
        if (!price || this.testText.test(price)) {
            return '';
        }
        price = String(price).replace(/[^0-9.]/g, '');
        if (price.includes('.')) {
            return '.' + (Number(price) % 1 || 0).toFixed(2).substr(2);
        }
        else {
            return '';
        }
    }
}
PriceCentsPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: PriceCentsPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
PriceCentsPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: PriceCentsPipe, name: "priceCents" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: PriceCentsPipe, decorators: [{
            type: Pipe,
            args: [{ name: 'priceCents' }]
        }] });

function checkForFallback(value, fallback) {
    return value === undefined ? fallback : value;
}
function param(str) {
    return str.replace(/\$.*\$/g, '') || undefined;
}
function paramNum(str) {
    return Number(param(str)) || undefined;
}
function paramBool(str) {
    const fixedStr = param(str);
    if (fixedStr === undefined) {
        return undefined;
    }
    if (fixedStr === 'true') {
        return true;
    }
    else {
        return false;
    }
}
const DEFAULT_RETRY_STRATEGY = {
    maxRetryAttempts: 3,
    scalingDuration: 500,
    excludedStatusCodes: [0, 403, 401],
    excludeHttpMessages: ['Forbidden', 'Unauthorized', 'Unknown Error'],
};
function getValuesFromPath(path) {
    const findLocation = path.indexOf('?') + 1 || path.indexOf('#') + 1;
    let cleanData = '';
    if (findLocation) {
        cleanData = path.slice(findLocation);
    }
    if (!cleanData) {
        return {};
    }
    const paramsList = cleanData
        .split('&')
        .reduce((prev, item) => {
        return Object.assign({ [item.split('=')[0]]: item.split('=')[1] }, prev);
    }, {});
    return paramsList || {};
}
const genericRetryStrategy = (retryObj = DEFAULT_RETRY_STRATEGY) => {
    const newRetryObj = { ...DEFAULT_RETRY_STRATEGY, ...retryObj };
    return {
        count: newRetryObj.maxRetryAttempts,
        delay: (error, retryAttempt) => {
            if ((error && retryAttempt > newRetryObj.maxRetryAttempts) ||
                (newRetryObj.excludedStatusCodes.find((e) => e === error?.status || (error?.error && e === error?.error?.status)) !== undefined &&
                    newRetryObj.excludeHttpMessages.find((m) => m === error?.statusText || m === error?.error?.httpMessage))) {
                return throwError(() => error);
            }
            if (get(error, 'error.errors[0].errorMessage')) {
                return throwError(() => error);
            }
            if (newRetryObj.customFunc) {
                return newRetryObj.customFunc(error, newRetryObj, retryAttempt);
            }
            return timer(retryAttempt * newRetryObj.scalingDuration);
        },
    };
};

const udeploy = {
    currency: {
        symbol: param('$default.currency$'),
        secondarySymbols: param('$default.secondarysymbols$'),
        bypass: param('$default.bypasscurrency$'),
    },
};

const environment = {
    currency: {
        symbol: udeploy.currency.symbol || '$',
        bypass: udeploy.currency.bypass || '',
        secondarySymbols: udeploy.currency.secondarySymbols || '',
    },
};

class PriceDollarsPipe {
    constructor() {
        this.testText = new RegExp('[a-z|A-Z]', 'gi');
        this.testCurrency = new RegExp('(\\d+.\\d+)', 'gi');
        this.bypassSymbols = environment.currency.bypass;
        this.ignoreSymbols = this.bypassSymbols.split(' ');
        this.currencySymbol = environment.currency.symbol;
        this.secondarySymbols = get(environment.currency, 'secondarySymbols', '').split('');
    }
    transform(price) {
        if (!price) {
            return price;
        }
        price = price.trim();
        const containsIgnoredSymbols = filter(this.ignoreSymbols, symbol => includes(price, symbol));
        if (!!containsIgnoredSymbols && containsIgnoredSymbols.length) {
            const expression = new RegExp('[^0-9.$. ' +
                this.bypassSymbols +
                this.currencySymbol +
                this.secondarySymbols +
                ']', 'gi');
            price = price.replace(expression, '');
            const containsSecondarySymbols = !!(find(this.secondarySymbols, symbol => includes(price, symbol)) || []).length;
            if (price.indexOf(this.currencySymbol) === -1 &&
                price.indexOf(this.secondarySymbols) === -1 &&
                !containsSecondarySymbols) {
                return this.currencySymbol + price;
            }
            const otherIgnorSymbols = containsIgnoredSymbols.filter(e => e !== ',');
            const hasCurrencySymbol = price.includes(this.currencySymbol);
            const hasSecondarySymbols = this.secondarySymbols.some((substring) => price.includes(substring));
            if ((hasCurrencySymbol || hasSecondarySymbols) &&
                otherIgnorSymbols.length) {
                return price;
            }
        }
        const testPrice = this.testText.test(price);
        if (!testPrice) {
            if (typeof price === 'string' || price instanceof String) {
                price = +price.replace(/[^0-9.]/g, '');
            }
            return this.currencySymbol + Math.floor(price).toLocaleString();
        }
        return price.replace(this.testCurrency, `${this.currencySymbol}$$&`);
    }
}
PriceDollarsPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: PriceDollarsPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
PriceDollarsPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: PriceDollarsPipe, name: "priceDollars" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: PriceDollarsPipe, decorators: [{
            type: Pipe,
            args: [{ name: 'priceDollars' }]
        }] });

const ignoreSymbols = environment.currency.bypass.split(' ');
const currencySymbol = environment.currency.symbol.split(' ');
const secondarySymbols = environment.currency.secondarySymbols.split('');
class PriceLegalMarksPipe {
    transform(price) {
        if (!price) {
            return '';
        }
        const expression = '[^0-9.$., ' + ignoreSymbols + currencySymbol + secondarySymbols + ']';
        const legal = String(price).match(new RegExp(expression, 'gi'));
        return legal ? legal.join('') : '';
    }
}
PriceLegalMarksPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: PriceLegalMarksPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
PriceLegalMarksPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: PriceLegalMarksPipe, name: "priceLegalMarks" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: PriceLegalMarksPipe, decorators: [{
            type: Pipe,
            args: [{ name: 'priceLegalMarks' }]
        }] });

class AppUtilsModule {
}
AppUtilsModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: AppUtilsModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
AppUtilsModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: AppUtilsModule, declarations: [PriceLegalMarksPipe, PriceDollarsPipe, PriceCentsPipe] });
AppUtilsModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: AppUtilsModule, imports: [[]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: AppUtilsModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [PriceLegalMarksPipe, PriceDollarsPipe, PriceCentsPipe],
                    imports: [],
                    exports: []
                }]
        }] });

class BaseComponent {
    constructor(ngZone) {
        this.ngZone = ngZone;
    }
    ngOnInit() { }
    ngOnDestroy() { }
}
BaseComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: BaseComponent, deps: [{ token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Directive });
BaseComponent.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.4.0", type: BaseComponent, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: BaseComponent, decorators: [{
            type: Directive
        }], ctorParameters: function () { return [{ type: i0.NgZone }]; } });

function AutoSubscription(options = { checkForAll: false }, observables$ = []) {
    return function (sourceClass) {
        const onInit = sourceClass.prototype.ngOnInit;
        const onDestroy = sourceClass.prototype.ngOnDestroy;
        if (typeof onDestroy === 'function') {
            const initFunction = () => {
                sourceClass.prototype['subscriptions'] = new Subscription();
            };
            initFunction();
            sourceClass.prototype.ngOnInit = function () {
                if (isEmpty(this.subscriptions['_subscriptions'])) {
                    initFunction();
                }
                if (onInit && typeof onInit === 'function') {
                    onInit.apply(this, arguments);
                }
            };
            sourceClass.prototype.ngOnDestroy = function () {
                if (this.subscriptions && this.subscriptions.unsubscribe) {
                    this.subscriptions.unsubscribe();
                }
                if (options && options.checkForAll) {
                    for (const prop in this) {
                        const property = this[prop];
                        if (typeof property.unsubscribe === 'function' &&
                            !observables$.includes(property)) {
                            observables$.push(property);
                        }
                    }
                    for (const observable$ of observables$) {
                        observable$.unsubscribe();
                    }
                }
                if (onDestroy && typeof onDestroy === 'function') {
                    onDestroy.apply(this, arguments);
                }
                initFunction();
            };
        }
        else {
            console.error('===========  ==========');
            console.error(sourceClass.name, 'must have ngOnDestroy implemented within the class, even if empty');
            console.error('===========  ==========');
        }
        return sourceClass;
    };
}

let SubscriptionComponent = class SubscriptionComponent extends BaseComponent {
};
SubscriptionComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: SubscriptionComponent, deps: null, target: i0.ɵɵFactoryTarget.Directive });
SubscriptionComponent.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.4.0", type: SubscriptionComponent, usesInheritance: true, ngImport: i0 });
SubscriptionComponent = __decorate([
    AutoSubscription()
], SubscriptionComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: SubscriptionComponent, decorators: [{
            type: Directive
        }] });

function devLogger(className, ...args) {
    if (isDevMode() || get(window.localStorage, 'enableDevDebugConsole')) {
        return console.warn(className, ...args);
    }
}

function ServiceGuard(options) {
    return function (sourceClass) {
        const proxy = new Proxy(sourceClass, {
            construct(oConstructor, args, target) {
                let error = '';
                const instance = Reflect.construct(oConstructor, args, target);
                instance.prototype = sourceClass.prototype;
                let serviceGuard = instance.prototype.serviceGuard;
                if (serviceGuard === undefined) {
                    serviceGuard = instance.serviceGuard;
                }
                const checkValue = function (check) {
                    if (!check) {
                        error =
                            'will not execute any functions except for bypass functions, and properties will stay read only, as service guard check was not a truthy value.';
                        devLogger(sourceClass.name, error);
                        const handler = {
                            get: function (oClass, property, _args2) {
                                let prop = Reflect.get(oClass, property);
                                if (prop === undefined) {
                                    prop = Reflect.get(sourceClass, property);
                                }
                                if (prop === undefined) {
                                    prop = args[property];
                                }
                                if (options &&
                                    options.bypassFunction &&
                                    options.bypassFunction.includes(property)) {
                                    return prop;
                                }
                                if (typeof prop === 'function') {
                                    return () => Promise.resolve();
                                }
                                return prop;
                            },
                        };
                        return new Proxy(instance, handler);
                    }
                    return instance;
                };
                if (options) {
                    if (options.checkValueOverride && !options.appDefaultCheck) {
                        if (instance.prototype[options.checkValueOverride] === undefined &&
                            instance[options.checkValueOverride] === undefined) {
                            error = `needs a method or property named  "${options.checkValueOverride}" with a truthy return`;
                        }
                        else {
                            if (instance.prototype[options.checkValueOverride] !== undefined) {
                                serviceGuard = instance.prototype[options.checkValueOverride];
                            }
                            serviceGuard = instance[options.checkValueOverride];
                        }
                    }
                    if (options.appDefaultCheck) {
                        if (!instance['appDefaults']) {
                            error =
                                'needs dependency called "appDefaults" to use appDefaultCheck';
                        }
                        else {
                            let check = true;
                            for (const key of options.appDefaultCheck) {
                                if (!get(instance['appDefaults'], key, '')) {
                                    check = false;
                                }
                            }
                            return checkValue(check);
                        }
                    }
                }
                if (serviceGuard !== undefined) {
                    if (typeof serviceGuard === 'function') {
                        const checkFunction = serviceGuard.apply(instance);
                        if (typeof checkFunction === 'object' &&
                            checkFunction.then) {
                            return checkFunction
                                .then((value) => {
                                return checkValue(value);
                            })
                                .catch(() => { });
                        }
                        return checkValue(checkFunction);
                    }
                    return checkValue(serviceGuard);
                }
                else {
                    error =
                        'needs a method or property named serviceGuard with a truthy return';
                }
                if (error) {
                    devLogger(sourceClass.name, error + ', in order to determine if it can pass the guard.');
                    return checkValue(false);
                }
            },
        });
        return proxy;
    };
}

const CacheKeys = {
    DEBUG_APP_ENV: 'DEBUG_APP_ENV',
    DEBUG_APP_ENV_RESET: 'DEBUG_APP_ENV_RESET',
    DEBUG_APP_ENV_CURRENT: 'DEBUG_APP_ENV_CURRENT',
    DEBUG_APP_ENV_SHARED: 'DEBUG_APP_ENV_SHARED',
    DEBUG_APP_ENV_SHARED_ALWAYS: 'DEBUG_APP_ENV_SHARED_ALWAYS',
    DEBUG_APP_ENV_KEY: 'DEBUG_APP_ENV_KEY',
};

const APP_ENV_REPLACED_DEFAULTS = {
    app_env: {
        content_id: param('$app_env.content_id$'),
        debug_key: param('$app_env.debug_key$'),
    },
};
const APP_ENV_ENV_DEFAULTS = {
    app_env: {
        content_id: '',
        debug_key: '',
    },
};

class AppEnvironmentService {
    constructor(customEnv, locationStrategy, localStorage) {
        this.customEnv = customEnv;
        this.locationStrategy = locationStrategy;
        this.localStorage = localStorage;
        this.envValues$ = new BehaviorSubject({});
        if (!isEmpty(this.customEnv)) {
            this.setupEnvDefaults(this.customEnv);
        }
        this.addDefaultValues(APP_ENV_ENV_DEFAULTS, APP_ENV_REPLACED_DEFAULTS);
        this.checkDebugValues();
    }
    get envValues() {
        return this._envValues;
    }
    set envValues(value) {
        this._envValues = value;
        if (this.debugEnv) {
            if (!isEmpty(this.debugValues)) {
                this._envValues = merge(this._envValues, this.debugValues);
            }
            this.localStorage.setItem(CacheKeys.DEBUG_APP_ENV_CURRENT, this._envValues);
        }
        this.envValues$.next(this._envValues);
    }
    setupEnvDefaults(defaults) {
        this._envValues = defaults;
    }
    getEnvVariables(key) {
        if (key && this._envValues[key]) {
            return this._envValues[key];
        }
        return this._envValues;
    }
    addDefaultValues(defaultObj, udeployObj) {
        const overrideDefaults = merge(defaultObj, this._envValues);
        const values = merge(overrideDefaults, udeployObj);
        this.envValues = values;
    }
    mergeWithCurrentEnv(values) {
        this.envValues = merge(this._envValues, values);
    }
    resetEnvValues() {
        this.localStorage.removeItem('oAuthToken');
        this.localStorage.removeItem('oAuthRefreshToken');
        this.localStorage.removeItem('HEADERS_WC_TOKEN');
        this.localStorage.removeItem('HEADERS_WC_TRUSTED_TOKEN');
        this.localStorage.removeItem('InteractGuestId');
        this.localStorage.removeItem('STORE_ID');
    }
    checkDebugValues() {
        this.debugEnv = this.localStorage.getItem(CacheKeys.DEBUG_APP_ENV, true);
        const keys = this.localStorage.getItem(CacheKeys.DEBUG_APP_ENV_KEY, true) || {};
        const base = this.locationStrategy.getBaseHref();
        if (!get(keys, base)) {
            keys[base] = this._envValues.app_env.debug_key;
            if (this.debugEnv) {
                this.localStorage.setItem(CacheKeys.DEBUG_APP_ENV_KEY, keys);
            }
        }
        else {
            this._envValues.app_env.debug_key = keys[base];
        }
        const appKey = this._envValues.app_env.debug_key;
        if (this.debugEnv && appKey) {
            const debugReset = this.localStorage.getItem(CacheKeys.DEBUG_APP_ENV_RESET, true);
            if (debugReset) {
                this.resetEnvValues();
            }
            this.localStorage.setItem(CacheKeys.DEBUG_APP_ENV_RESET, false);
            this.appCacheKey = `${CacheKeys.DEBUG_APP_ENV}_${appKey}`;
            const shared = this.localStorage.getItem(CacheKeys.DEBUG_APP_ENV_SHARED, true) || {};
            const sharedAlways = this.localStorage.getItem(CacheKeys.DEBUG_APP_ENV_SHARED_ALWAYS, true) || {};
            if (isEmpty(shared)) {
                this.localStorage.setItem(CacheKeys.DEBUG_APP_ENV_SHARED, {});
            }
            if (isEmpty(sharedAlways)) {
                this.localStorage.setItem(CacheKeys.DEBUG_APP_ENV_SHARED_ALWAYS, {});
            }
            const app = this.localStorage.getItem(this.appCacheKey, true);
            const mergedValues = merge(shared, app);
            const completedValues = merge(mergedValues, sharedAlways);
            this.debugValues = completedValues;
            this.localStorage.setItem(this.appCacheKey, completedValues);
        }
    }
}
AppEnvironmentService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: AppEnvironmentService, deps: [{ token: 'CustomEnv', optional: true }, { token: i1.LocationStrategy }, { token: i2.LocalStorageService }], target: i0.ɵɵFactoryTarget.Injectable });
AppEnvironmentService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: AppEnvironmentService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: AppEnvironmentService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: ['CustomEnv']
                }] }, { type: i1.LocationStrategy }, { type: i2.LocalStorageService }]; } });

class JasmineBaseHelper {
    constructor(fixture, expect, spyOn) {
        this.fixture = fixture;
        this.de = this.fixture.debugElement;
        this.detectChanges();
        this.expect = expect;
        this.spy = spyOn;
    }
    spyOn(component, method, ...args) {
        this.spy(component, method).and.callThrough();
        component[method](...args);
        this.expect(component[method])[args.length > 0 ? 'toHaveBeenCalledWith' : 'toHaveBeenCalled'](...args);
    }
    toBeCloseTo(arg, precision) {
        this.doExpect('toBeCloseTo', this.getValue(false, true), false, false, arg, precision);
    }
    toBeGreaterThan(arg) {
        this.doExpect('toBeGreaterThan', this.getValue(false, true), false, false, arg);
    }
    toBeGreaterThanOrEqual(arg) {
        this.doExpect('toBeGreaterThanOrEqual', this.getValue(false, true), false, false, arg);
    }
    toBeInstanceOf(arg) {
        this.doExpect('toBeInstanceOf', false, false, false, arg);
    }
    toBeLessThan(arg) {
        this.doExpect('toBeLessThan', false, false, false, arg);
    }
    toBeLessThanOrEqual(arg) {
        this.doExpect('toBeLessThanOrEqual', false, false, false, arg);
    }
    toBeNaN() {
        this.doExpect('toBeNaN');
    }
    toBeNegativeInfinity() {
        this.doExpect('toBeNegativeInfinity');
    }
    toBeNull() {
        this.doExpect('toBeNull');
    }
    toBePositiveInfinity() {
        this.doExpect('toBePositiveInfinity');
    }
    toBeTrue() {
        this.doExpect('toBeTrue');
    }
    toBeUndefined() {
        this.doExpect('toBeUndefined');
    }
    toBeDefined() {
        this.doExpect('toBeDefined');
    }
    toContain(arg, stripTags = false) {
        this.doExpect('toContain', false, stripTags, false, arg);
    }
    toMatch(arg, stripTags = false) {
        this.doExpect('toMatch', false, stripTags, false, arg);
    }
    toBe(arg, fakeTest = true, stripTags = false) {
        this.doExpect('toBe', false, stripTags, fakeTest, arg);
    }
    toEqual(arg, fakeTest = true) {
        this.doExpect('toEqual', false, false, fakeTest, arg);
    }
    lengthToBe(value, fakeTest = true) {
        this.doExpect('toBe', this.el ? this.el.length : undefined, fakeTest, false, value);
    }
    toBeTruthy() {
        this.expect(this.getElement()).toBeTruthy();
    }
    toBeFalsy() {
        this.expect(this.getElement()).toBeFalsy();
    }
    toHaveClass(expected) {
        this.expect(this.getElement()).toHaveClass(expected);
    }
    toBeFalse() {
        this.expect(this.getElement()).toBeFalse();
    }
    changeSelectOption(keyIndex) {
        let result;
        if (this.el) {
            const el = this.getElement();
            result = el.value = this.getSelectOption(keyIndex);
            el.dispatchEvent(new Event('change'));
            this.detectChanges();
        }
        return result;
    }
    click() {
        this.detectChanges();
        this.getElement().triggerEventHandler('click', null);
        this.detectChanges();
    }
    getLength() {
        return !this.el ? 0 : this.el.length;
    }
    getValue(stripTags = false, isInt = false) {
        const getContent = () => {
            const tagName = get(this.el, 'nativeElement.tagName', undefined);
            return get(this.el, 'nativeElement.' +
                (tagName && tagName.toLowerCase() === 'input'
                    ? 'value'
                    : 'textContent'), undefined);
        };
        return this.el
            ? stripTags || isInt
                ? stripTags
                    ? this.stripHtmlTags(getContent())
                    : parseInt(getContent(), 10)
                : getContent()
            : undefined;
    }
    getElement() {
        return this.el ? get(this.el, 'nativeElement', undefined) : undefined;
    }
    getByClassName(className, neededData = false, tag = '', isSingle = true) {
        const domElement = this.getDomElement(isString(tag) ? `${tag}.${className}` : `.${className}`, neededData, isSingle);
        return neededData ? domElement : this;
    }
    getById(idEl, neededData = false, tag = '', isSingle = true) {
        const domElement = this.getDomElement(isString(tag) ? `${tag}#${idEl}` : `#${idEl}`, neededData, isSingle);
        return neededData ? domElement : this;
    }
    getByTag(tag, neededData = false, isSingle = true) {
        const domElement = this.getDomElement(tag, neededData, isSingle);
        return neededData ? domElement : this;
    }
    getByAttr(tag, attrKey, attrValue, neededData = false, isSingle = true) {
        const domElement = this.getDomElement(`${tag}[${attrKey}=${attrValue}]`, neededData, isSingle);
        return neededData ? domElement : this;
    }
    getDomElement(stringPath, neededData, isSingle = true) {
        this.detectChanges();
        const domElement = (this.el = this.de[isSingle ? 'query' : 'queryAll'](By.css(stringPath)));
        return domElement && neededData && isString(neededData)
            ? get(domElement, neededData)
            : this;
    }
    doExpect(funcName, expected, stripTags, fakeTest, ...args) {
        const getExpected = () => {
            return expected
                ? stripTags
                    ? this.stripHtmlTags(expected)
                    : expected
                : this.getValue(stripTags);
        };
        if (args && typeof args[0] !== 'undefined') {
            for (const x in args) {
                if (args.hasOwnProperty(x)) {
                    args[x] = stripTags ? this.stripHtmlTags(args[x]) : args[x];
                }
            }
            this.expect(getExpected())[funcName](...args);
        }
        else {
            if (fakeTest) {
                this.expect(true).toBe(true);
            }
            else {
                this.expect(getExpected()).toBe(...args);
            }
        }
    }
    getSelectOption(key) {
        if (!this.el) {
            return undefined;
        }
        return key
            ? this.getElement()['options'][key]['value']
            : this.getElement()['options'];
    }
    stripHtmlTags(str) {
        return Number.isInteger(str)
            ? str
            : str.replace(/(<([^>]+)>)/gi, '').trim();
    }
    stripLines(str) {
        return Number.isInteger(str) ? str : str.replace(/\r?\n|\r/g, '').trim();
    }
    fakeTest(expVal = true, value = true) {
        this.expect(expVal).toBe(value);
    }
    detectChanges() {
        this.fixture.detectChanges();
    }
}

function waitForElement(selector, parentElement, intervalTime, debounceTime, subtree = true) {
    return new Promise((resolve, reject) => {
        const intervalCheck = intervalTime || 1000;
        const debounceCheck = debounceTime || 100;
        const parentNode = parentElement || document.body;
        const element = findElement(selector, parentNode);
        if (element) {
            resolve(element);
        }
        else {
            const intervalTimer = interval(intervalCheck)
                .pipe(takeUntil(timer(10000)))
                .subscribe(() => {
                const element2 = findElement(selector, parentNode);
                if (element2) {
                    resolve(element2);
                    intervalTimer.unsubscribe();
                }
            }, error => {
                console.log('Error finding element:', error);
                reject(error);
            }, () => setupObserver());
            const setupObserver = () => {
                const observer = new MutationObserver(debounce((mutationList) => {
                    const mutation = mutationList.reduce((_, record) => Array.from(record.addedNodes).find(node => node instanceof HTMLElement && node?.matches(selector)), undefined);
                    !resolveFunc(mutation) &&
                        resolveFunc(findElement(selector, parentNode));
                }, debounceCheck, { leading: true }));
                observer.observe(parentNode, {
                    childList: true,
                    subtree
                });
                const resolveFunc = (found) => {
                    if (found) {
                        resolve(found);
                        observer.disconnect();
                        return true;
                    }
                    return false;
                };
            };
        }
    });
}
function watchForElement(selector, parentElement, debounceTime) {
    const debounceWait = debounceTime || 100;
    const subject = new Subject();
    const parentNode = parentElement || document.body;
    const findElem = () => {
        const elements = findElements(selector, parentNode);
        elements.length && subject.next(elements);
    };
    const observer = new MutationObserver(debounce(() => requestAnimationFrame(() => findElem()), debounceWait));
    observer.observe(parentNode, {
        childList: true,
        subtree: true
    });
    subject['_oldUnsubscribe'] = subject.unsubscribe;
    subject.unsubscribe = () => {
        observer.disconnect();
        subject['_oldUnsubscribe']();
    };
    return subject;
}
function watchForAttributeChange(attribute, element, debounceTime) {
    const debounceWait = debounceTime || 100;
    const subject = new BehaviorSubject(element);
    const observer = new MutationObserver(debounce((mutationList) => {
        if (attribute === '') {
            subject.next(element);
        }
        else {
            const mutation = mutationList.reduce((_, record) => record.attributeName === attribute ? record.target : undefined, undefined);
            mutation && subject.next(mutation);
        }
    }, debounceWait));
    observer.observe(element, {
        attributes: true,
        attributeFilter: attribute ? [attribute] : undefined
    });
    subject['_oldUnsubscribe'] = subject.unsubscribe;
    subject.unsubscribe = () => {
        observer.disconnect();
        subject['_oldUnsubscribe']();
    };
    return subject;
}
function findElement(selector, parent) {
    return document.getElementById(selector) || parent.querySelector(selector);
}
function findElements(selector, parent) {
    return findElement(selector, parent)
        ? Array.from(parent.querySelectorAll(selector))
        : [];
}

function sendEvent(name, detail, options = {}) {
    return window.dispatchEvent(new CustomEvent(name, {
        detail,
        ...options
    }));
}

export { APP_ENV_ENV_DEFAULTS, AppEnvironmentService, AppUtilsModule, AutoSubscription, BaseComponent, CacheKeys, DEFAULT_RETRY_STRATEGY, JasmineBaseHelper, ServiceGuard, SubscriptionComponent, checkForFallback, devLogger, genericRetryStrategy, getValuesFromPath, param, paramBool, paramNum, sendEvent, waitForElement, watchForAttributeChange, watchForElement };
